/**
 * Finding somebody.
 *
 * Everybody here is a person who signs in — never a candidate. Candidates have
 * no account, no name and no address anywhere in this system.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Spinner from '../../components/Spinner';
import { Button, Chip, Empty, Field, Input, Notice } from '../../components/ui';
import { api, describeError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import ConsoleLayout from './ConsoleLayout';

interface Person {
  id: string;
  email: string;
  displayName: string | null;
  isPlatformAdmin: boolean;
  disabledAt: string | null;
  lastSeenAt: string | null;
  organizationCount: number;
}

export default function People() {
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<Person[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ people: Person[] }>(
        `/api/console/people?q=${encodeURIComponent(query)}`,
      );
      setPeople(result.people);
      setError(null);
    } catch (failure) {
      setError(describeError(failure));
    }
  }, [query]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 200);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <ConsoleLayout back={{ to: '/console', label: 'Estate' }}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[clamp(1.5rem,1.2rem+1vw,2rem)]">People</h1>
          <p className="mt-1.5 max-w-prose text-[13px] leading-relaxed text-bone/45">
            Everybody who can sign in. Candidates are not here and never will be — they have no
            account, and no name or address is stored for them anywhere.
          </p>
        </div>
        <Button onClick={() => setCreating(!creating)}>
          {creating ? 'Cancel' : 'Add an account'}
        </Button>
      </header>

      {creating && (
        <div className="mb-6">
          <CreatePerson
            onCreated={() => {
              setCreating(false);
              void load();
            }}
          />
        </div>
      )}

      {error && (
        <div className="mb-5">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by address or name"
        className="mb-5 max-w-md"
      />

      {!people ? (
        <Spinner label="Searching" />
      ) : people.length === 0 ? (
        <Empty title="Nobody matches." />
      ) : (
        <ul className="divide-y divide-veil/[0.06]">
          {people.map((person) => (
            <li key={person.id} className="flex flex-wrap items-center gap-3 py-3">
              <Link
                to={`/console/people/${person.id}`}
                className="min-w-0 flex-1 truncate hover:text-bone"
              >
                <span className="block truncate text-[14px]">
                  {person.displayName ?? person.email}
                </span>
                {person.displayName && (
                  <span className="block truncate font-code text-[11.5px] text-bone/35">
                    {person.email}
                  </span>
                )}
              </Link>
              {person.isPlatformAdmin && <Chip tone="accent">Staff</Chip>}
              {person.disabledAt && <Chip tone="danger">Disabled</Chip>}
              <span className="text-[12px] text-bone/30">
                {person.organizationCount === 0
                  ? 'no workspace'
                  : `${person.organizationCount} workspace${person.organizationCount === 1 ? '' : 's'}`}
              </span>
              <span className="text-[12px] text-bone/25">
                {person.lastSeenAt ? `seen ${formatDate(person.lastSeenAt)}` : 'never signed in'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </ConsoleLayout>
  );
}

function CreatePerson({ onCreated }: { onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/console/people', { email, displayName });
      onCreated();
    } catch (failure) {
      setError(describeError(failure));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="panel p-5">
      {error && (
        <div className="mb-4">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}
      <p className="mb-4 max-w-prose text-[12.5px] leading-relaxed text-bone/45">
        This records an account before its owner has ever signed in, so they can be seated or
        invited as an owner ahead of time. It does not create a credential — that stays with the
        identity provider, and the two meet when they register with the same address.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Name">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <div className="flex items-end pb-1">
          <Button type="submit" variant="primary" busy={busy}>
            Add
          </Button>
        </div>
      </div>
    </form>
  );
}
